/*
Usage Notes: 

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  <-plan-picker 
    product='{{ product | json | escape }}'
    selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
    formId='{{ product_form_id }}'
    >
  </-plan-picker>
  <script src="{{ '-plan-picker-component.js' | asset_url }}" type="module"></script>

  Note: formId not required if element is inside of a form already
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';
// LitJS Core https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js
// LitJS ALL (includes directives) https://cdn.jsdelivr.net/gh/lit/dist@2/all/lit-all.min.js

const Styles = css`
  :host {
    display: block;
    margin-bottom: 1rem;
  }

  .-plan-picker {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0;
    border: 0;
    color: #000b44;
  }
  .-onetime-second {
    order: 2;
  }
  
  .-group-container {
    display: none;
  }
  .-group-container--available {
    display: block;
    position: relative;
    border-radius: 3px;
    border-width: 1px;
    border-color: #ccc;
    border-style: solid; 
    transition: border-color 0.2s ease;
  }
  .-group-container--selected {
    border-color: #000622;
  }
  
  .-group-input {
    position: absolute;
    width: 0px;
    height: 0px;
    opacity: 0;
  }
  .-group-input:focus-visible ~ .-group-label {
    outline: 2px #ccc solid;
    outline-offset: 4px;
    border-radius: 5px;
  }
  
  .-group-label {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    padding: 10px;
    overflow: hidden;
  }
  
  .-group-topline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    font-size: 16px;
  }
  
  .-radio__container {
    display: flex;
    margin-right: 10px;
  }
  
  .-radio {
    transition: transform 0.25s cubic-bezier(0.4,0,0.2,1), opacity 0.25s cubic-bezier(0.4,0,0.2,1);
    transform-origin: center;
    transform: scale(0);
    opacity: 0;
  }
  .-group-label:hover .-radio {
    transform: scale(1);
    opacity: 0.75;
  }
  .-group-container--selected .-group-label .-radio {
    transform: scale(1);
    opacity: 1;
  }
  
  .-price {
    margin-left: auto;
  }
  
  .-group-content {
    width: auto;
    margin-left: 30px;
    transition: max-height 0.25s cubic-bezier(0.4,0,0.2,1),
                opacity 0.25s cubic-bezier(0.4,0,0.2,1);
    max-height: 38px;
    opacity: 1;
  }
  
  /* Hide frequency if not selected */
  .-group-container:not(.-group-container--selected) .-group-content {
    max-height: 0;
    opacity: 0;
    pointer-events: none;
  }
  
  .-group-title {
    min-width: max-content;
  }
  
  .-save {
    /* color: #80ebb3; */
    background: #9bddff;
    color: #000b44;
    /* border: 1px #80ebb3 solid;  */
    padding: 2px 10px;
    border-radius: 20px;
  }
  
  .-frequency {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 8px 30px 8px 10px;
    margin-top: 5px;
    border-radius: 5px;
    background-color: #f7f7f7;
    width: 100%;
    border: 0;
    color: #000b44 !important;
    font-size: 14px;
    white-space: nowrap;
    text-overflow: ellipsis;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
    background-position: right 10px top 50%;
    background-size: 18px;
    background-repeat: no-repeat;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
  }
  .-frequency.-frequency--one {
    background-image: none;
  }
  
  .-frequency span {
    text-transform: lowercase;
  }
`;

export class PlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object },            //required
    productHandle: { type: String },      //optional (unless product isn't passed, then required)
    key: { type: String },                //optional, defaults to product.id; identifier for this instance of the  plan picker

    subscriptionFirst: { type: Boolean }, //optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, //optional, defaults to false; if true, auto-selects subscription on page load
    hideSelect: { type: Boolean }, // optional, defaults to false; if true, hides the select dropdown
    discountFormat: { type: String },     //optional, defaults to percent; can also pass "fixed"
    
    currency: { type: String },           //optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String },           //optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {},                   //placeholder for object
  
    selectedVariant: { type: Object },    //placeholder for data
    SellingPlanGroups: {},            //placeholder for data
    availableSellingPlanGroups: {},       //placeholder for data
    selectedSellingPlanGroup: {},         //placeholder for data
    selectedSellingPlan: {},              //placeholder for data

    formId: { type: String }
  };

  static styles = Styles;

  constructor() {
    super();
    
    this.product = null;
    this.selectedVariant = null;

    this.productHandle = null;

    this.key = null;

    this.SellingPlanGroups = null;
    this.availableSellingPlanGroups = null;

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = false;
    this.subscriptionFirst = false;
    this.hideSelect = false;

    this.MainProduct = true;

    this.discountFormat = 'percent';

    this.currency = 'USD';
    this.language = 'en-US';
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
    });
  }

  connectedCallback() {
    super.connectedCallback();

    if(!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle);
    }

    this.bindFormEvents();
  }

  render() {
    if(!this.product || !this.selectedVariant) return;
    
    return html`
      <fieldset class="lazy-item -plan-picker" -plan-picker="${ this.key }">
        <div class="lazy-item -group-container ${ this.product.requires_selling_plan == false ? '-group-container--available' : '' } ${ this.selectedSellingPlanGroup == null ? '-group-container--selected' : '' } ${ this.subscriptionFirst ? '-onetime-second' : ''}" -group-container 
          @click=${() => this.selectSellingPlanGroup(null) } >
        
          <input id="-one-time-${ this.key }" class="lazy-item -group-input" name="-group-${ this.key }" type="radio" value="" 
            -one-time ?checked=${ this.startSubscription == false && this.product.requires_selling_plan == false ? true : false }>
          <label class="lazy-item -group-label" for="-one-time-${ this.key }">
            <div class="lazy-item -group-topline">
              <div class="lazy-item -radio__container">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
                  <circle class="lazy-item -radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
                </svg>
              </div>
              One-time
              <div class="lazy-item -price">
                <span -onetime-price>${ this.moneyFormatter.format(this.selectedVariant.price / 100) }</span>
              </div>
            </div>
          </label>
        </div>
        ${ this.availableSellingPlanGroups ? this.availableSellingPlanGroups.map((group, index) => 
          html`
            <div class="lazy-item -group-container -group-container--available ${ this.selectedSellingPlanGroup == group ? '-group-container--selected' : '' }" -group-container
              @click=${() => this.selectSellingPlanGroup(group) }>
              <input id="-selling-plan-group-${ index }-${ this.key }" class="lazy-item -group-input" name="-group-${ this.key }"
                type="radio" value="${ group.id }" -selling-plan-group="${ group.id }" ?checked=${ this.selectedSellingPlanGroup == group ? true : false } >
              <label class="lazy-item -group-label" for="-selling-plan-group-${ index }-${ this.key }">
                <div class="lazy-item -group-topline">
                  <div class="lazy-item -radio__container">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"></circle>
                      <circle class="lazy-item -radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
                    </svg>
                  </div>
                  <div class="lazy-item -group-title">
                    ${ group.name }
                    ${ this.discount(group.selected_selling_plan).percent !== '0%' ? html`
                      <span class="lazy-item -save">Save <span -discount>${ this.discountFormat == 'percent' ? this.discount(group.selected_selling_plan).percent : this.discount(group.selected_selling_plan).amount }</span></span>
                    ` : html`` }
                  </div>
                  <div class="lazy-item -price">
                    <span -subscription-price>${ this.price(group.selected_selling_plan) }</span>
                  </div>
                </div>
                <div class="lazy-item -group-content">
                  ${ !this.hideSelect ? html`
                  <select -selling-plans="${ group.id }" class="lazy-item -frequency ${this.getAvailableSellingPlans(group).length === 1 ? '-frequency--one' : ''}"
                    @change=${ (e) => this.selectSellingPlan(e.target, group) }>
                    ${ group ? this.getAvailableSellingPlans(group).map((selling_plan) => 
                        html`
                        <option value="${ selling_plan.id }">
                          ${ group.name == 'Subscription' ? `FREE Shipping - ${ selling_plan.name }` : `${ selling_plan.name }` }
                        </option>
                        `
                      ): ''}
                  </select>
                  ` : '' }
                </div>
              </label>
            </div>
          `
        ): ''}
      </fieldset>`
  }

  getAvailableSellingPlans = (group) => {
    return group.selling_plans.filter((selling_plan) =>this.selectedVariant.selling_plan_allocations.some((allocation) => allocation.selling_plan_id == selling_plan.id))
  }

  updated = (changed) => {
    if(changed.has('product') && this.product) {
      //update key
      this.key = this.key ? this.key : this.product.id;

      //update SellingPlanGroups
      this.SellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group => selling_plan_group.app_id === ''
      )
    }

    if(changed.has('selectedVariant') && this.selectedVariant) {
      //update availableSellingPlanGroups based on SellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = this.SellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(
            selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id
          )
        )
      )

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length > 0) {
        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach((group => {
          let availableSellingPlans = group.selling_plans.filter(selling_plan =>
            this.selectedVariant.selling_plan_allocations.some(
              selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id
            )
          );

          group.selected_selling_plan = availableSellingPlans[0];
        }));

        if (this.startSubscription == true || this.product.requires_selling_plan == true || this.selectedSellingPlan) {
          this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];
          this.selectedSellingPlan = this.availableSellingPlanGroups[0].selected_selling_plan;
        } else {
          this.selectedSellingPlan = null;
          this.selectedSellingPlanGroup = null
        }
      } else {
        this.selectedSellingPlan = null;
        this.selectedSellingPlanGroup = null
      }

      this.updateForm();
    }

    if(changed.has('selectedSellingPlan')) {
      //update price of price elements if applicable
      document.querySelectorAll(`[-price][-key="${ this.key }"]`).forEach((el) => {
        el.innerHTML = this.price(this.selectedSellingPlan);
      });

      //update display of external content elements
      document.querySelectorAll(`[-onetime-content][-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan !== null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      document.querySelectorAll(`[-subscription-content][-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan == null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`::update-selling-plan`, {
        bubbles: true, 
        composed: true, 
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key
        }
      });

      this.dispatchEvent(event);

      this.updateForm();
    }
  }

  log = (...args) => {
    args.unshift('%c[]', 'color: #8770f2;');
    console.log.apply(console, args);
  }

  error = (...args) =>  {
    args.unshift('%c []', 'color: #ff0000');
    console.error.apply(console, args);
  }
  
  // Update selected selling plan group; called on click of -group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;

    //update the form that was passed, if any
    this.updateForm();
  }

  // Update selected selling plan; called on change of -frequency select element
  selectSellingPlan(element, group) {
    let selling_plan = group.selling_plans.find(x => x.id == element.value);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
    }
    else this.log("Error: couldn't find selling plan with id " + element.value + " for variant " + this.selectedVariant.id + " from product " + this.product.id + " : " + this.product.handle);
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan)
      return { percent: '0%', amount: 0 }
    
    const price_adjustment = selling_plan.price_adjustments[0]
    const discount = { percent: '0%', amount: 0 }
    const price = this.selectedVariant.price;
    const compareAtPrice = this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > this.selectedVariant.price ? this.selectedVariant.compare_at_price : this.selectedVariant.price;
    
    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = `${price_adjustment.value}%`
        discount.amount = Math.round(
          (price * price_adjustment.value) / 100.0
        )
        break
      case 'fixed_amount':
        discount.percent = `${Math.round(
          ((price_adjustment.value * 1.0) / price) * 100.0
        )}%`
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = `${Math.round(
          (((compareAtPrice - price_adjustment.value) * 1.0) /
            compareAtPrice) *
            100.0
        )}%`
        discount.amount = price - price_adjustment.value
        break
    }
    
    return discount
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price(selling_plan, formatted = true) {
    return formatted
      ? this.money( this.selectedVariant.price - this.discount(selling_plan).amount)
      :  this.selectedVariant.price - this.discount(selling_plan).amount
  }

  // Updates element data to be registered by forms
  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]');
    if($sellingPlan) this.querySelector('input[name=selling_plan]').value = this.selectedSellingPlan ? this.selectedSellingPlan.id : '';
  }

  bindFormEvents() {
    this.form =
      document.querySelector(`#${this.formId}`) ||
      this.closest('form[action*="/cart/add"]')

    this.log(' Form: ', this.form)
      
    if (!this.form) {
      return
    }

    this.variantInput = this.form.querySelector('[name="id"]')

    if (this.variantInput) {
      this.variantInput.addEventListener('change', (e) => {
        this.selectedVariant = this.product.variants.find((variant) => variant.id == e.target.value);
        this.variantChanged = true;
      })
    }
  }

  // Optional functions keep if necessary 

  /**
   *   
   * 
   */
  
  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan?.id })
      }
    ];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items })
    })
    .then((response) => response.json())
    .then((response) => {
      this.log(" added item to cart: ", response);
      //dispatch CustomEvent to tell document that an item was added to cart
      const event = new CustomEvent(`::added-to-cart`, {
        bubbles: true, 
        composed: true, 
        detail: {
          response,
          key: this.key
        }
      });

      this.dispatchEvent(event);
    })
    .catch((error) => {
      this.error(` ${ this.key } error adding item to cart: `, error);
    });
  } 

  fetchProduct = (handle) => {
    return fetch(`/products/${ handle }.js`)
    .then((response) => response.json())
    .then((product) => {
      this.product = product;
      this.selectedVariant = product.variants[0];

      return product;
    });
  }
}

customElements.define('-plan-picker', PlanPickerComponent);